import React, { useContext } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { MessageContext } from '../chat-messages';

type MessageFileProps = {
    isMe: boolean;
    message: any;
};

// generate the icon of file type
const fileIconGenerator = (fileType: string | undefined) => {
    switch (fileType) {
        case 'png':
        case 'jpg':
        case 'jpeg':
            return <ImageOutlinedIcon sx={{ width: 18, height: 18 }} />;
        case 'pdf':
            return <PictureAsPdfIcon sx={{ width: 18, height: 18 }} />;
        case 'docx':
            return <ArticleIcon sx={{ width: 18, height: 18 }} />;
        default:
            return (
                <InsertDriveFileOutlinedIcon sx={{ width: 18, height: 18 }} />
            );
    }
};

const MessageFile = ({ isMe, message }: MessageFileProps) => {
    const messageHook = useContext(MessageContext);
    const {
        getFileNameFromURL,
        onGetFileFormat,
        onDownloadFile,
        isSending,
        onDeleteOrRestore,
        userInfo,
    } = messageHook!;

    const fileDownloadSx = {
        width: '100%',
        display: 'flex',
        flexDirection: isMe ? 'row' : 'row-reverse',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '8px',
        boxShadow: '0px 0px 5px 1px rgba(0,0,0,0.1)',
        px: 1,
        py: 0.5,
        my: 0.2,
    };

    const fileTitleSx = {
        display: 'flex',
        flexGrow: 1,
        mr: isMe ? 1 : 0.4,
        ml: isMe ? 0.4 : 1,
        mt: 0.4,
        fontSize: 14,
        maxWidth: '150px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    return (
        message?.files &&
        message.files.map((file: any, i: number) => {
            const isFileString: boolean = typeof file === 'string';

            const fileTitle = !!file
                ? getFileNameFromURL(!isFileString ? file?.file : file)
                : 'فایل';

            const isDownloadable: boolean =
                file?.status === 'active' &&
                message?.status === 'active' &&
                !message?.isNotSent;

            return (
                (file?.status === 'active' || userInfo?.isAdmin) && (
                    <Box key={i} sx={{ width: '100%' }}>
                        <Box
                            sx={{
                                ...fileDownloadSx,
                                backgroundColor: 'background.default',
                                opacity:
                                    file?.status !== 'active' ||
                                    message?.status !== 'active' ||
                                    isSending
                                        ? 0.6
                                        : 1,
                            }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: isMe ? 'row' : 'row-reverse',
                                    alignItems: 'center',
                                }}>
                                {fileIconGenerator(
                                    onGetFileFormat(
                                        !isFileString ? file?.file : file,
                                    ),
                                )}

                                {/* <Tooltip title={fileTitle} arrow> */}
                                <Typography variant="body1" sx={fileTitleSx}>
                                    {fileTitle}
                                </Typography>
                                {/* </Tooltip> */}
                            </Box>

                            <IconButton
                                disabled={!isDownloadable}
                                size="small"
                                onClick={() =>
                                    onDownloadFile(
                                        !isFileString ? file?.file : file,
                                        fileTitle,
                                    )
                                }
                                sx={{
                                    cursor: isDownloadable
                                        ? 'pointer'
                                        : 'default',
                                    p: 0,
                                    '&.MuiButtonBase-root:hover': {
                                        bgcolor: 'transparent',
                                    },
                                }}>
                                <SaveAltIcon sx={{ width: 18, height: 18 }} />
                            </IconButton>

                            {onDeleteOrRestore && (
                                <FileMenu
                                    message={message}
                                    file={file}
                                    onDeleteOrRestore={onDeleteOrRestore}
                                />
                            )}
                        </Box>
                    </Box>
                )
            );
        })
    );
};

export default MessageFile;

export const FileMenu = ({ message, file, onDeleteOrRestore }: any) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div>
            <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                    p: 0,
                    mr: 0.5,
                    '&.MuiButtonBase-root:hover': {
                        bgcolor: 'transparent',
                    },
                }}
                disabled={message?.status !== 'active'}>
                <MoreVertIcon sx={{ width: 18, height: 18 }} />
            </IconButton>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {file?.status === 'active' ? (
                    <MenuItem
                        sx={menuItemSx}
                        onClick={() => {
                            onDeleteOrRestore(file?.id, 'delete');
                            handleClose();
                        }}>
                        حذف
                    </MenuItem>
                ) : (
                    <MenuItem
                        sx={menuItemSx}
                        onClick={() => {
                            onDeleteOrRestore(file?.id, 'restore');
                            handleClose();
                        }}>
                        بازگردانی
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};

const menuItemSx = { fontSize: 12, py: 0.2, px: 1, minHeight: 0 };
